import { Show, createEffect, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { Grid } from '../../../grid-system/grid/grid';
import { Section } from '../../../grid-system/section/section';
import { CrossIcon } from '../../icons-library/cross';
import { LoginForm } from '../../login-form/login-form';
import { Heading } from '../../../ui-components/heading/heading';
import theme from '../../../style/theme';
import { TabHandler } from '../../../tools/tab-handler';
import { NewAtosLogoWhite } from '../../icons-library/new-atos-logo-white';

export const LoginMenuDesktop = (props: LoginMenuProps) => {
    const { localize, siteInfo, viewport, userState } = useContext(AppContext);
    const [ welcomeMessage, setWelcomeMessage ] = createSignal<string>('');

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    const isLargeDesktop = () => {
        // Must match the grid responsive breakpoint (1270px) to avoid CSS/render mismatch
        return viewport.width > theme.breakpoints.DESKTOP;
    };

    createEffect(() => {
        if (siteInfo.siteType === 'default') {
            setWelcomeMessage(localize('welcome-to-healthcare-professionals', 'Welcome Healthcare Professionals!'));
        } else {
            setWelcomeMessage(localize('welcome-to-atos-care', 'Welcome to Atos Care'));
        }
    });


    const Header = () => (
        <div class="flex justify-end items-center min-h-cvi-14 py-cvi-7 sticky right-0 top-0">
            <button
                class="flex items-center gap-cvi-2 p-cvi-5 cursor-pointer text-cvi-fg-default hover:text-cvi-fg-link"
                onClick={() => props.handleCloseMenuClick()}
                ref={(el: HTMLElement) => setFirstTabbableElement(el)}
            >
                <CrossIcon fill="currentColor" size="medium" ariaHidden />
                <span class="label-md font-cvi-medium pt-cvi-1">{localize('close', 'Close')}</span>
            </button>
        </div>
    );

    const WelcomeText = () => (
        <div class="mt-[50%] mb-[25%]">
            <Heading tag='h1' variant='heading-4xl' class="text-cvi-fg-on-color">
                {welcomeMessage()}
            </Heading>
        </div>
    );

    const Logo = () => (
        <div class="flex flex-col">
            <div class="w-auto relative">
                <NewAtosLogoWhite/>
            </div>
        </div>
    );

    const LoginCtaText = () => (
        <Show when={!userState.user}>
            <div class="border-b border-cvi-border-on-color" />
            <p class="body-lg font-cvi-regular text-cvi-fg-on-color mt-cvi-9">
                <Show
                    fallback={localize('log-in-to-access-healthcare-professionals', 'Log in to view resources for healthcare professionals')}
                    when={siteInfo.siteType === 'atos-care'}
                >
                    {localize('log-in-to-access-atos-care', 'Log in to access Atos Care')}
                </Show>
            </p>
        </Show>
    );

    return (
        <Section
            templateShorthand={[12]}
            widthType={'bgFull'}
            removeSpacingBlock={true}
            heightType="fill"
            customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
        >
            <TabHandler
                refs={{
                    firstTabbableElement,
                    lastTabbableElement,
                }}
            >
                <Grid
                    templateShorthand={[4, 1, 7]}
                    customCss={'height: 100%;'}
                    responsive={{
                        largeDesktop: [4, 1, 7],
                        smallDesktop: [4, 8],
                        tablet: [12],
                        mobile: [12],
                    }}
                >
                    <div class="bg-cvi-bg-dark-muted h-full box-border pt-cvi-9 pr-cvi-13 max-[920px]:pr-cvi-9 relative">
                        <Logo />

                        <WelcomeText/>

                        <LoginCtaText />

                        <div class="absolute top-0 bottom-0 w-[10000px] ml-[-10000px] bg-cvi-bg-dark-muted" />
                    </div>

                    <Show when={isLargeDesktop()}>
                        <div />
                    </Show>

                    <div class="h-screen overflow-scroll [&::-webkit-scrollbar]:hidden">
                        <Header/>

                        <LoginForm
                            firstElement={(el: HTMLElement) => {
                                setFirstTabbableElement(el);
                            }}
                            lastElement={(el: HTMLElement) => {
                                setLastTabbableElement(el);
                            }}
                        />

                        <div class="sticky bottom-0 left-0 w-full h-[80px] pointer-events-none bg-linear-to-b from-white/20 to-white" />

                    </div>
                </Grid>
            </TabHandler>
        </Section>
    );
};
export type LoginMenuProps = {
    handleCloseMenuClick: () => void;
    handleSignOutClick: () => void;
};
